/**
 * Module dependencies.
 */

import { useTranslation } from 'next-i18next';
import LocationIcon from 'src/assets/landingpage/location-icon.svg';
import ReportIcon from 'src/assets/landingpage/report-icon.svg';
import VerificationIcon from 'src/assets/landingpage/verification-icon.svg';
import styled from 'styled-components';

/**
 * `Container` styled component.
 */

const Container = styled.div`
  /* stylelint-disable no-descending-specificity */
  column-gap: 44px;
  display: grid;
  grid-template-areas:
    'icon1 icon2 icon3'
    'text1 text2 text3';
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: auto auto;
  row-gap: 0;
  width: 100%;

  @media (max-width: 767px) {
    column-gap: 32px;
    grid-template-areas:
      'icon1 text1'
      'icon2 text2'
      'icon3 text3';
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto auto;
  }

  .cell {
    display: flex;
    position: relative;
  }

  .cell:nth-child(1) {
    grid-area: icon1;
  }

  .cell:nth-child(2) {
    grid-area: icon2;
    justify-content: center;
  }

  .cell:nth-child(3) {
    grid-area: icon3;
    justify-content: flex-end;
  }

  .cell:nth-child(4) {
    grid-area: text1;
  }

  .cell:nth-child(5) {
    grid-area: text2;
  }

  .cell:nth-child(6) {
    grid-area: text3;
  }

  @media (max-width: 767px) {
    .cell:nth-child(4),
    .cell:nth-child(5),
    .cell:nth-child(6) {
      margin-left: 0;
      margin-right: 0;
    }
  }

  @media (max-width: 374px) {
    .cell:nth-child(4),
    .cell:nth-child(5),
    .cell:nth-child(6) {
      margin-left: 0;
    }
  }

  .cell:nth-child(1)::before {
    background-color: #00cddc;
    bottom: 0;
    content: '';
    left: 32px;
    position: absolute;
    right: -22px;
    top: 0;

    @media (max-width: 767px) {
      left: 0;
      right: 0;
      top: 32px;
    }

    @media (max-width: 374px) {
      top: 20px;
    }
  }

  .cell:nth-child(2)::before {
    background-color: #00cddc;
    bottom: 0;
    content: '';
    left: -22px;
    position: absolute;
    right: -22px;
    top: 0;

    @media (max-width: 767px) {
      left: 0;
      right: 0;
    }
  }

  .cell:nth-child(3)::before {
    background-color: #00cddc;
    bottom: 0;
    content: '';
    left: -22px;
    position: absolute;
    right: 32px;
    top: 0;

    @media (max-width: 767px) {
      bottom: auto;
      height: 32px;
      left: 0;
      right: 0;
    }

    @media (max-width: 374px) {
      height: 20px;
    }
  }

  .cell > div > p {
    color: #0a0a28;
    font-family: Raleway, sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    margin-bottom: 16px;
    margin-top: 8px;

    @media (max-width: 767px) {
      margin-top: 8px;
      min-height: 70px;
    }
  }

  .icon {
    height: 63.64px;
    position: relative;
    width: 63.64px;
    z-index: 1;

    @media (max-width: 374px) {
      height: 40px;
      width: 40px;
    }

    > div:first-child {
      inset: 0;
      position: absolute;

      svg {
        display: block;
        height: 100%;
        width: 100%;
      }
    }
  }

  .title-text {
    color: #0a0a28;
    font-family: Raleway, sans-serif;
    font-size: 24px;
    font-weight: 900;
    line-height: 32px;
    margin-top: 16px;

    @media (max-width: 374px) {
      margin-top: 6px;
    }

    p {
      margin-bottom: 0;

      &:last-child {
        margin-bottom: initial;
      }
    }
  }

  .icon.location > div:last-child {
    aspect-ratio: 14 / 23;
    left: 29.17%;
    overflow: clip;
    position: absolute;
    right: 30.75%;
    top: 10.36px;

    @media (max-width: 374px) {
      top: 6.51px;
    }

    > div {
      display: contents;
      inset: 0;
      position: absolute;

      svg {
        display: block;
        height: 100%;
        width: 100%;
      }
    }
  }

  .icon.verification > div:last-child {
    inset: 16.67%;
    overflow: clip;
    position: absolute;

    > div {
      bottom: -0.01%;
      left: 0;
      position: absolute;
      right: -0.01%;
      top: 0;

      svg {
        display: block;
        height: 100%;
        width: 100%;
      }
    }
  }

  .icon.report > div:last-child {
    aspect-ratio: 21 / 29;
    left: 27.08%;
    overflow: clip;
    position: absolute;
    right: 29.17%;
    top: 13.31px;

    @media (max-width: 374px) {
      top: 8.37px;
    }

    > div {
      bottom: 0;
      display: contents;
      left: 0;
      position: absolute;
      right: 0.01%;
      top: 0;

      > div {
        bottom: 0;
        left: 0;
        position: absolute;
        right: 0.01%;
        top: 0;

        svg {
          display: block;
          height: 100%;
          width: 100%;
        }
      }
    }
  }
  /* stylelint-enable no-descending-specificity */
`;

/**
 * Export `Steps` component.
 */

export function Steps() {
  const { t } = useTranslation('landingpage');

  return (
    <Container>
      <div className={'cell'}>
        <div className={'icon verification'}>
          <VerificationIcon />
        </div>
      </div>
      <div className={'cell'}>
        <div className={'icon location'}>
          <LocationIcon />
        </div>
      </div>
      <div className={'cell'}>
        <div className={'icon report'}>
          <ReportIcon />
        </div>
      </div>
      <div className={'cell'}>
        <div>
          <div className={'title-text'}>
            <p>{t('stepper.steps.0.title')}</p>
          </div>
          <p>{t('stepper.steps.0.description')}</p>
        </div>
      </div>
      <div className={'cell'}>
        <div>
          <div className={'title-text'}>
            <p>{t('stepper.steps.1.title')}</p>
          </div>
          <p>{t('stepper.steps.1.description')}</p>
        </div>
      </div>
      <div className={'cell'}>
        <div>
          <div className={'title-text'}>
            <p>{t('stepper.steps.2.title')}</p>
          </div>
          <p>{t('stepper.steps.2.description')}</p>
        </div>
      </div>
    </Container>
  );
}
